import { taskKey } from "./platform-tasks.mjs";
import { taskSolutionVersion } from "./solution-version.mjs";

export const METRIC_OUTCOMES = [
  ["ready", "Готово"], ["warning", "В работе / замечания"],
  ["error", "Ошибка"], ["neutral", "В очереди / остановлен"],
];

export function metricOutcome(task) {
  if (task.status === "completed") return "ready";
  if (["running", "retry_wait", "suspended", "partial", "completed_partial"].includes(task.status)) return "warning";
  if (["failed", "timed_out"].includes(task.status)) return "error";
  return "neutral";
}

export function readyMetricTask(task) {
  return !task.solutionDescriptor?.integrationOnly &&
    !task.solutionId?.startsWith("solution_triproutes_") &&
    !task.solutionId?.startsWith("solution_corplinks_");
}

// Fetch every page independently of the Tasks/Results list and its filters.
// The existing APIs enforce session scope and platform-admin authorization.
export async function readReadyMetricHistory(api, { platform = false, isCurrent = () => true } = {}) {
  const tasks = new Map();
  const cursors = new Set();
  let cursor = "";
  do {
    if (!isCurrent()) return null;
    const params = new URLSearchParams({ limit: "200" });
    if (cursor) params.set("cursor", cursor);
    const page = await api.request(`${platform ? "/v1/platform/tasks" : "/v1/tasks"}?${params}`);
    if (!isCurrent()) return null;
    if (!Array.isArray(page.items)) throw new Error("Не удалось прочитать историю запусков.");
    for (const task of page.items) if (readyMetricTask(task)) tasks.set(taskKey(task), task);
    cursor = page.nextCursor || "";
    if (cursor && cursors.has(cursor)) throw new Error("Не удалось загрузить историю целиком. Повторите обновление.");
    cursors.add(cursor);
  } while (cursor);
  return [...tasks.values()];
}

export function taskMetricCost(task) {
  const cost = task.cost?.executionCost || task.cost || {};
  const raw = cost.amount;
  const currency = String(cost.currency || "").toUpperCase();
  if (cost.coverage === "mixedCurrencies" || raw == null || String(raw).trim() === "" ||
      !currency || !Number.isFinite(Number(raw)) || Number(raw) < 0) return null;
  return { amount: Number(raw), currency, partial: cost.coverage === "partial" || ["partial", "preliminary"].includes(cost.status) || cost.coverage !== "complete" && !["calculated", "final", "reconciled"].includes(cost.status) };
}

export function filterMetricHistory(tasks, { solutionId = "", solutionVersion = "", days = "all" } = {}, now = Date.now()) {
  const cutoff = days === "all" ? null : now - Number(days) * 86400000;
  return tasks.filter((task) => (!solutionId || task.solutionId === solutionId) &&
    (!solutionVersion || taskSolutionVersion(task) === solutionVersion) &&
    (cutoff == null || Date.parse(task.createdAt) >= cutoff));
}

export function metricSolutionVersions(tasks, solutionId = "") {
  return [...new Set(tasks.filter((task) => !solutionId || task.solutionId === solutionId)
    .map(taskSolutionVersion).filter(Boolean))].sort((a, b) => b.localeCompare(a, "ru", { numeric: true }));
}

export function buildReadyMetrics(tasks) {
  const counts = Object.fromEntries(METRIC_OUTCOMES.map(([key]) => [key, 0]));
  const costs = new Map();
  let unknownCosts = 0, partialCosts = 0, missingNumbers = 0;
  const points = [];
  for (const task of tasks) {
    const tone = metricOutcome(task);
    counts[tone]++;
    const cost = taskMetricCost(task);
    if (cost) {
      costs.set(cost.currency, (costs.get(cost.currency) || 0) + cost.amount);
      if (cost.partial) partialCosts++;
    } else unknownCosts++;
    if (!Number.isSafeInteger(task.launchNumber) || task.launchNumber < 1) { missingNumbers++; continue; }
    // Unknown, mixed and non-USD expenses never become zero-dollar points.
    points.push({ task, number: task.launchNumber, version: taskSolutionVersion(task), tone, cost: cost?.currency === "USD" ? cost : null });
  }
  points.sort((a, b) => a.number - b.number);
  return { total: tasks.length, counts, costs: [...costs].map(([currency, amount]) => ({ currency, amount })),
    unknownCosts, partialCosts, missingNumbers, points, plotted: points.filter((point) => point.cost).length };
}

export function launchCostGeometry(points, containerWidth = 720) {
  const width = Math.max(720, containerWidth, points.length * 44 + 120);
  const height = 320, margin = { left: 78, right: 28, top: 28, bottom: 54 };
  const min = points[0]?.number || 1, max = points.at(-1)?.number || min;
  const maximumCost = points.reduce((max, point) => Math.max(max, point.cost?.amount || 0), 0.01);
  const x = (number) => margin.left + (max === min ? (width - margin.left - margin.right) / 2 :
    (number - min) / (max - min) * (width - margin.left - margin.right));
  const y = (cost) => height - margin.bottom - cost / maximumCost * (height - margin.top - margin.bottom);
  const paths = [];
  let path = "";
  for (const point of points) {
    if (!point.cost) { if (path) paths.push(path); path = ""; continue; }
    path += `${path ? " L" : "M"}${x(point.number)},${y(point.cost.amount)}`;
  }
  if (path) paths.push(path);
  return { width, height, margin, maximumCost, x, y, paths };
}
